import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";

/**
 * Pasek konta tymczasowego.
 *
 * Konto gościa działa jak każde inne, ale jedynym kluczem do niego jest token
 * w tej przeglądarce — wyczyszczone dane strony albo inne urządzenie znaczą
 * utratę wszystkiego. To musi być powiedziane wprost, a nie schowane, bo
 * użytkownik podejmuje decyzję o tym, gdzie zapisuje swoją pracę.
 *
 * Założenie konta nie przenosi danych: dopisuje e-mail i hasło do rekordu,
 * który już istnieje.
 */
const GuestBanner = () => {
  const { user, token, login } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  // Zamknięcie na sesję, nie na stałe — ryzyko utraty danych jest realne i
  // ma wracać przy każdym nowym otwarciu aplikacji, ale nie musi zajmować
  // miejsca na stałe, skoro użytkownik już je raz przeczytał.
  const [dismissed, setDismissed] = useState(false);

  if (!user?.is_guest || dismissed) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api(
        "post",
        "/auth/claim",
        { email: form.email, password: form.password, name: form.name || undefined },
        token
      );
      login(res.data.access_token, res.data.user);
      setOpen(false);
      toast.success("Konto założone — dane zostały na miejscu");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nie udało się założyć konta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        data-testid="guest-banner"
        style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          padding: "10px 14px", marginBottom: 16, borderRadius: 8,
          background: "var(--warning-bg, #fff7ed)",
          border: "1px solid var(--warning-border, #fed7aa)",
        }}
      >
        <div style={{ flex: 1, minWidth: 220, fontSize: 13, lineHeight: 1.5 }}>
          <strong>Konto tymczasowe.</strong>{" "}
          Wszystko, co zapiszesz, żyje tylko w tej przeglądarce. Załóż konto, żeby
          mieć dostęp z innego urządzenia i nie stracić danych.
        </div>
        <Button size="sm" data-testid="guest-claim-btn" onClick={() => setOpen(true)} style={{ gap: 6 }}>
          <UserPlus size={15} /> Załóż konto
        </Button>
        <button type="button" data-testid="guest-banner-dismiss" onClick={() => setDismissed(true)}
                aria-label="Zamknij"
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 4 }}>
          <X size={16} />
        </button>
      </div>

      {open && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
                   alignItems: "center", justifyContent: "center", zIndex: 100 }}
          onClick={() => setOpen(false)}
        >
          <Card style={{ width: "100%", maxWidth: 400, margin: 16 }} onClick={(e) => e.stopPropagation()}>
            <CardContent style={{ padding: 24 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>Załóż konto</h3>
                <button type="button" onClick={() => setOpen(false)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)" }}>
                  <X size={18} />
                </button>
              </div>
              <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16, lineHeight: 1.5 }}>
                Nic nie przenosimy — to jest to samo konto, tylko od teraz z hasłem.
              </p>
              <form onSubmit={submit}>
                <div className="field">
                  <Label>Imię i nazwisko <span className="optional">(opcjonalne)</span></Label>
                  <Input data-testid="claim-name-input" value={form.name}
                         placeholder={user?.name || ""}
                         onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="field">
                  <Label>Email</Label>
                  <Input type="email" required data-testid="claim-email-input" value={form.email}
                         onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="field">
                  <Label>Hasło</Label>
                  <Input type="password" required data-testid="claim-password-input" value={form.password}
                         onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <Button type="button" variant="outline" style={{ flex: 1 }} onClick={() => setOpen(false)}>
                    Anuluj
                  </Button>
                  <Button type="submit" disabled={saving} style={{ flex: 1 }} data-testid="claim-submit-btn">
                    {saving ? "Zakładam..." : "Załóż konto"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default GuestBanner;
